import {
    Badge,
    Box,
    ButtonBase, IconButton, Skeleton, styled,
    Typography,
    useMediaQuery
} from "@mui/material";
import { H6, Small, Tiny } from "components/Typography";
import AppAvatar from "components/avatars/AppAvatar";
import FlexBox from "components/flexbox/FlexBox";
import useAuth from "hooks/useAuth";
import { Fragment, memo, useContext, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PopoverLayout from "./PopoverLayout"; // styled components
import { LogoutOutlined, EditOutlined } from "@mui/icons-material";
import ThemeIcon from "icons/ThemeIcon";
import { SettingsContext } from "contexts/settingsContext";
import { Stack } from "@mui/system";
import { memoize } from "lodash";
import LanguagePopover from "./LanguagePopover";

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  }));

  const StyledButtonBase = styled(ButtonBase)(({ theme }) => ({
    padding: 5,
    marginLeft: 4,
    borderRadius: 30,
    border: `1px solid ${theme.palette.divider}`,
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
}));
const StyledSmall = styled(Small)(({ theme }) => ({
    display: "block",
    cursor: "pointer",
    padding: "5px 1rem",
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
}));

const AvatarContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    display: "inline-block",
    cursor: "pointer",
    "&:hover .edit-icon": {
        opacity: 1,
    },
}));

const EditIcon = styled(IconButton)(({ theme }) => ({
    position: "absolute",
    top: 8,
    right: 8,
    backgroundColor: theme.palette.background.paper,
    opacity: 0,
    transition: "opacity 0.2s ease-in-out",
    boxShadow: theme.shadows[2],
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
    "& .MuiSvgIcon-root": {
        fontSize: 16,
    },
}));

const ProfilePopoverV2 = ( { compact } ) => {

    const { logout, user } = useAuth();
    const { settings, saveSettings } = useContext(SettingsContext);
    
    const handleChangeTheme = (value) => {
        saveSettings({ ...settings, theme: value, themeSource: "user" });
    };

    if(user && !user.username) {
        console.log(JSON.stringify(user));
    }

    return (
        <Box
            sx={{
                padding: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                margin: "auto",
                width: "90%"
            }}
        >
            <Badge
                overlap="circular"
                variant="dot"
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                sx={{
                    alignItems: "center",
                    "& .MuiBadge-badge": {
                        width: 11,
                        height: 11,
                        right: "4%",
                        borderRadius: "50%",
                        border: "2px solid #fff",
                        backgroundColor: "success.main",
                    },
                    margin:"auto"
                }}
            >
                <AppAvatar
                    src={user.profile.profile_picture || "/static/avatar/001-man.svg"}
                    sx={{
                        width: 28,
                        height: 28,
                    }}
                />
            </Badge>

            {!compact && (
            <Box mx={2}>
                <H6>{user.profile.name}</H6>
            </Box>
            )}
            {!compact && (
            settings.theme === "light" ? (
                <StyledIconButton onClick={() => handleChangeTheme("dark")}>
                <ThemeIcon />
                </StyledIconButton>
            ) : (
                <StyledIconButton onClick={() => handleChangeTheme("light")}>
                <ThemeIcon />
                </StyledIconButton>
            ))}
            {!compact && (
            <StyledIconButton
                onClick={() => {
                    logout();
                    toast.error("You Logout Successfully");
                }}
                sx={{margin: "auto"}}
            >
                <LogoutOutlined fontSize="small"/>
            </StyledIconButton>
            )}
        </Box>
    );
}

const ProfilePopoverV3 = ( { compact } ) => {
    const { t } = useTranslation();
    const { logout, user, isIndieAdmin, isAgentUser, isClient } = useAuth();
    const { settings, saveSettings } = useContext(SettingsContext);
    const navigate = useNavigate();
    
    const handleChangeTheme = (value) => {
        saveSettings({ ...settings, theme: value, themeSource: "user" });
    };

    const handleEditProfile = () => {
        navigate("/account/client/profile/personal-data");
    };

    const getUserRoleLabel = memoize(() => {
        if (isIndieAdmin()) return t("label.role.admin");
        if (isAgentUser()) return t("label.role.agent");
        if (isClient()) return t("label.role.client");
        return "";
    });

    const roleTypes = {
        CLIENT: "label.form.client",
        ENERGY_AGENT_USER: "label.menu.energyAgent",
        INDIE_ADMIN: "Admin",
    };

    return (
        <Stack spacing={2} mb={14} width="100%" direction="column" alignItems="center">
            <AvatarContainer onClick={handleEditProfile}>
                <AppAvatar
                    src={user.profile.profile_picture || "/static/avatar/001-man.svg"}
                    sx={{
                        width: compact ? 50 : 110,
                        height: compact ? 50 : 110,
                    }}
                />
                <EditIcon className="edit-icon" size="small">
                    <EditOutlined />
                </EditIcon>
            </AvatarContainer>

            {!compact && (
                <Box textAlign="center">
                    <Typography fontSize={15} fontWeight="bold">{user.profile.name}</Typography>
                    <Typography fontSize={14}>{getUserRoleLabel()}</Typography>
                </Box>
            )}
            <Stack direction="row">
                {!compact && (
                    <LanguagePopover languageCode={user.profile.language_code} userID={user.profile.id} />
                )}
                {settings.theme === "light" ? (
                        <StyledIconButton onClick={() => handleChangeTheme("dark")}>
                            <ThemeIcon />
                        </StyledIconButton>
                    ) : (
                        <StyledIconButton onClick={() => handleChangeTheme("light")}>
                            <ThemeIcon />
                        </StyledIconButton>
                    )
                }
                <StyledIconButton
                    onClick={() => {
                        logout();
                        toast.error("You Logout Successfully");
                    }}
                    sx={{margin: "auto"}}
                >
                    <LogoutOutlined fontSize="small"/>
                </StyledIconButton>
            </Stack>
        </Stack>
    );
}

const ProfilePopover = () => {

    const anchorRef = useRef(null);
    const navigate = useNavigate();
    const { logout, user } = useAuth();
    const [open, setOpen] = useState(false);
    const upSm = useMediaQuery((theme) => theme.breakpoints.up("sm"));
    const { t } = useTranslation();
    const handleMenuItem = (path) => {
        navigate(path);
        setOpen(false);
    };

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    return (
        <Fragment>
            <StyledButtonBase
                disableRipple
                ref={anchorRef}
                onClick={() => setOpen(true)}
            >
                <Badge
                    overlap="circular"
                    variant="dot"
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    sx={{
                        alignItems: "center",
                        "& .MuiBadge-badge": {
                            width: 11,
                            height: 11,
                            right: "4%",
                            borderRadius: "50%",
                            border: "2px solid #fff",
                            backgroundColor: "success.main",
                        },
                    }}
                >
                    {upSm && (
                        <Small mx={1} color="text.secondary">
                            {t("label.layout.greeting")},{" "}
                            <Small fontWeight="600" display="inline">
                                {user.profile.name}
                            </Small>
                        </Small>
                    )}
                    <AppAvatar
                        src={user.profile.profile_picture || "/static/avatar/001-man.svg"}
                        sx={{
                            width: 28,
                            height: 28,
                        }}
                    />
                </Badge>
            </StyledButtonBase>

            <PopoverLayout
                hiddenViewButton
                maxWidth={230}
                minWidth={200}
                popoverOpen={open}
                anchorRef={anchorRef}
                popoverClose={() => setOpen(false)}
                title={
                    <FlexBox alignItems="center" gap={1}>
                        <AppAvatar
                            src={user.profile.profile_picture || "/static/avatar/001-man.svg"}
                            sx={{
                                width: 35,
                                height: 35,
                            }}
                        />

                        <Box>
                            <H6>{user.profile.name}</H6>
                            <Tiny display="block" fontWeight={500} color="text.disabled">
                                {user.email}
                            </Tiny>
                        </Box>
                    </FlexBox>
                }
            >
                <Box pt={1}>
                    
                    <StyledSmall
                        onClick={() => {
                            handleLogout();
                            toast.error("You Logout Successfully");
                        }}
                    >
                        {t("Logout")}
                    </StyledSmall>
                </Box>
            </PopoverLayout>
        </Fragment>
    );
};

export { ProfilePopoverV2 };
export { ProfilePopoverV3 };
export default ProfilePopover;
